import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ContarPorEmpaque } from '../Empaques';
import { almacen, newOperationId, type CountSession } from '../api';
import {
  Boton,
  Cabecera,
  Cargando,
  ConAyuda,
  Fallo,
  Filtro,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import { cantidad, contados, dia, diaDeLima, hora, soles } from '../formato';
import { leerCantidad } from '../numeros';
import { Escaner } from '../Escaner';
import { MatrizParaTeclear } from '../MatrizParaTeclear';
import { deLaTemporada, familiasEntre, insumosDeFamilias, temporadasDe } from '../prendas';
import { FiltroDeTemporada } from '../Temporada';
import { mayuscula, useVocabulario } from '../vocabulario';
import { fechaDeCalendario } from '../vencimiento';

/**
 * Contar el almacén y ajustar lo que no cuadre.
 *
 * **El conteo se abre con una foto del libro.** Antes se comparaba lo contado
 * contra el stock del momento de cerrar: lo que cocina vendía mientras se
 * contaba salía como sobrante, el stock quedaba inflado y el informe de fugas
 * lo leía al revés. Ahora al empezar se guarda qué decía el libro de cada
 * insumo, y al cerrar la diferencia se toma contra esa foto.
 *
 * **Se cuenta a ciegas.** Quien ve el número que «debería» haber cuenta hasta
 * llegar a él. Lo que dice el libro se enseña al revisar, cuando ya se contó.
 *
 * **Lo contado se guarda solo.** Un conteo de la cámara lleva una hora; antes,
 * recargar la página o que la tablet se durmiera borraba todo lo tecleado.
 * Ahora se retoma donde se dejó, desde este equipo o desde otro.
 */
export function Conteo() {
  const abierto = useQuery({ queryKey: ['conteo'], queryFn: almacen.currentCount });

  return (
    <>
      <Cabecera titulo="Conteo físico" subtitulo="Contar a ciegas y ajustar contra la foto del libro" ayuda="Se cuenta lo que hay de verdad sin ver lo que dice el sistema. Al cerrar, cada diferencia queda como ajuste en el historial." />

      <div className="cuerpo">
        {abierto.isError ? (
          <Fallo error={abierto.error} onReintentar={() => abierto.refetch()} />
        ) : abierto.isLoading ? (
          <Cargando que="el conteo" />
        ) : abierto.data === null || abierto.data === undefined ? (
          <EmpezarConteo />
        ) : (
          <ConteoAbierto conteo={abierto.data} />
        )}
      </div>
    </>
  );
}

/** Qué contar: todo, una categoría o una ubicación. */
function EmpezarConteo() {
  const vocabulario = useVocabulario();
  const clientes = useQueryClient();
  const opciones = useQuery({ queryKey: ['opciones'], queryFn: almacen.options });
  const insumos = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });

  const [categoryId, setCategoryId] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [countId, setCountId] = useState(newOperationId);

  const ubicaciones = [
    ...new Set((insumos.data ?? []).map((insumo) => insumo.location).filter((l): l is string => l !== null)),
  ].sort();

  const empezar = useMutation({
    mutationFn: () =>
      almacen.startCount({
        countId,
        document: `CNT-${diaDeLima().replace(/-/g, '')}-${hora(new Date().toISOString()).replace(':', '')}`,
        categoryId: categoryId === '' ? null : categoryId,
        location: ubicacion === '' ? null : ubicacion,
      }),
    onSuccess: () => {
      setCountId(newOperationId());
      void clientes.invalidateQueries({ queryKey: ['conteo'] });
    },
  });

  return (
    <Tarjeta>
      <h2 className="tarjeta__titulo">Empezar un conteo</h2>
      <p className="nota">
        Al empezar se guarda lo que dice el libro de cada {vocabulario.insumo} en este momento. Lo que se venda
        mientras cuentas no cambia la cuenta: al cerrar, la diferencia se toma contra esa foto.
      </p>

      <div className="formulario">
        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="conteo-categoria" ayuda="Para contar solo una parte, por ejemplo las carnes. Lo que no entra en el conteo no se toca al cerrar.">Categoría</Rotulo>
            <select id="conteo-categoria" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">Todas</option>
              {(opciones.data?.categories ?? []).map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.name}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <Rotulo htmlFor="conteo-ubicacion" ayuda="Para contar solo una zona, por ejemplo la cámara. Lo que no entra en el conteo no se toca al cerrar.">Dónde</Rotulo>
            <select id="conteo-ubicacion" value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}>
              <option value="">Todo el local</option>
              {ubicaciones.map((lugar) => (
                <option key={lugar} value={lugar}>
                  {lugar}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="nota-sm nota--pegada">
          Contar solo la cámara o solo los secos es un conteo de verdad: lo que no entra en él no se
          toca al cerrar.
        </p>

        {empezar.isError ? <Fallo error={empezar.error} /> : null}

        <Boton variante="primario" onClick={() => empezar.mutate()} disabled={empezar.isPending}>
          {empezar.isPending ? 'Abriendo…' : 'Empezar a contar'}
        </Boton>
      </div>
    </Tarjeta>
  );
}

type Paso = 'contando' | 'revisando';

function ConteoAbierto({ conteo }: { conteo: CountSession }) {
  const vocabulario = useVocabulario();
  const clientes = useQueryClient();
  const [paso, setPaso] = useState<Paso>('contando');

  /*
   * Lo tecleado, por insumo. Arranca con lo que ya se guardó: quien retoma el
   * conteo ve lo que dejó, no una lista en blanco.
   */
  // Los empaques de cada insumo, para contar rollos cerrados (§2.52). La
  // misma consulta que la lista: ya suele estar en memoria.
  const insumos = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });
  const [borrador, setBorrador] = useState<Record<string, string>>(() =>
    Object.fromEntries([
      ...conteo.lines.filter((linea) => linea.counted !== null).map((linea) => [linea.supplyId, paraTeclear(linea.counted!)]),
      // Lo ya contado lote por lote (migración 273).
      ...conteo.lines.flatMap((linea) =>
        (linea.lots ?? [])
          .filter((lote) => lote.counted !== null)
          .map((lote) => [claveDelLote(linea.supplyId, lote.lotId), paraTeclear(lote.counted!)]),
      ),
    ]),
  );
  /** Los insumos que se cuentan lote por lote: abiertos si ya se contó alguno. */
  const [porLote, setPorLote] = useState<Set<string>>(
    () =>
      new Set(
        conteo.lines
          .filter((linea) => (linea.lots ?? []).some((lote) => lote.counted !== null))
          .map((linea) => linea.supplyId),
      ),
  );
  const [sinGuardar, setSinGuardar] = useState<Set<string>>(new Set());
  const [busqueda, setBusqueda] = useState('');
  const [soloFaltan, setSoloFaltan] = useState(false);
  const [ultimo, setUltimo] = useState<string | null>(null);
  const [sinEncontrar, setSinEncontrar] = useState<string | null>(null);

  /*
   * Las prendas de este conteo, en matriz de tallas por colores. Contar el
   * polo era buscar «Polo básico ·» y teclear doce filas casi iguales; en la
   * matriz se teclea como se mira el estante. Lo tecleado va al mismo
   * borrador, por insumo, y se guarda igual. Un servidor sin prendas no
   * tiene la ruta: la lista de siempre.
   */
  const prendas = useQuery({ queryKey: ['prendas'], queryFn: almacen.variantFamilies });
  const familiasDelConteo = useMemo(
    () => familiasEntre(prendas.data ?? [], new Set(conteo.lines.map((linea) => linea.supplyId))),
    [prendas.data, conteo.lines],
  );
  const [porPrenda, setPorPrenda] = useState(false);
  const [temporada, setTemporada] = useState<string | null>(null);
  const enMatriz = porPrenda ? insumosDeFamilias(familiasDelConteo) : new Set<string>();

  const guardar = useMutation({
    mutationFn: (lineas: { clave: string; supplyId: string; lotId: string | null; counted: string | null }[]) =>
      almacen.saveCountLines(
        conteo.id,
        lineas.map(({ supplyId, lotId, counted }) => ({ supplyId, counted, ...(lotId === null ? {} : { lotId }) })),
      ),
    onSuccess: (_resultado, lineas) => {
      setSinGuardar((previo) => {
        const siguen = new Set(previo);
        for (const linea of lineas) {
          // Si se volvió a teclear mientras se guardaba, sigue pendiente.
          const ahora = (borradorRef.current[linea.clave] ?? '').trim().replace(',', '.');
          if (ahora === (linea.counted ?? '')) siguen.delete(linea.clave);
        }
        return siguen;
      });
    },
  });

  const borradorRef = useRef(borrador);
  borradorRef.current = borrador;

  /*
   * Se guarda solo, un momento después de dejar de teclear o de escanear. Un
   * guardado por cada lectura del escáner serían veinte peticiones por caja.
   */
  useEffect(() => {
    if (sinGuardar.size === 0 || guardar.isPending) return;

    const temporizador = window.setTimeout(() => {
      const listas = [...sinGuardar]
        .map((clave) => ({ clave, ...partirClave(clave), texto: (borradorRef.current[clave] ?? '').trim() }))
        // Vacío es «no lo he contado»; lo que no es un número se queda sin guardar.
        .filter((linea) => linea.texto === '' || !Number.isNaN(leerCantidad(linea.texto)))
        .map((linea) => ({
          clave: linea.clave,
          supplyId: linea.supplyId,
          lotId: linea.lotId,
          counted: linea.texto === '' ? null : linea.texto.replace(',', '.'),
        }));

      if (listas.length > 0) guardar.mutate(listas);
    }, 800);

    return () => window.clearTimeout(temporizador);
  }, [sinGuardar, guardar.isPending, guardar.mutate]);

  /** `clave` es el insumo, o el insumo y su lote (`claveDelLote`). */
  const teclear = (clave: string, texto: string) => {
    setBorrador((previo) => ({ ...previo, [clave]: texto }));
    setSinGuardar((previo) => new Set(previo).add(clave));
  };

  /** Las claves que tiene un insumo en el borrador: la suya y la de cada lote. */
  const clavesDe = (linea: CountSession['lines'][number]): string[] => [
    linea.supplyId,
    ...(linea.lots ?? []).map((lote) => claveDelLote(linea.supplyId, lote.lotId)),
  ];

  /**
   * Un código leído suma uno a ese insumo.
   *
   * **Suma en vez de escribir.** Quien cuenta pasa la lata, la deja, pasa la
   * siguiente: lo que se lee es cada envase. Si el escáner escribiera la
   * cantidad, cada lectura borraría la anterior y el conteo daría uno siempre.
   *
   * Solo entre lo de este conteo: una lata de otra categoría no se cuela en el
   * conteo de la cámara.
   */
  const alEscanear = (codigo: string) => {
    const limpio = codigo.trim().toLowerCase();
    const linea = conteo.lines.find(
      (una) => (una.barcode ?? '').toLowerCase() === limpio || una.sku.toLowerCase() === limpio,
    );

    if (linea === undefined) {
      setSinEncontrar(codigo);
      setUltimo(null);
      return;
    }

    setSinEncontrar(null);
    setUltimo(linea.supplyId);
    const actual = leerCantidad(borradorRef.current[linea.supplyId] ?? '0');
    teclear(linea.supplyId, paraTeclear(String((Number.isNaN(actual) ? 0 : actual) + 1)));
  };

  const contada = (linea: CountSession['lines'][number]) =>
    clavesDe(linea).some((clave) => (borrador[clave] ?? '').trim() !== '');
  const contadas = conteo.lines.filter(contada).length;
  const invalidas = conteo.lines.filter((linea) =>
    clavesDe(linea).some((clave) => {
      const texto = borrador[clave] ?? '';
      return texto.trim() !== '' && Number.isNaN(leerCantidad(texto));
    }),
  );

  const buscado = busqueda.trim().toLowerCase();
  const visibles = conteo.lines.filter(
    (linea) =>
      !enMatriz.has(linea.supplyId) &&
      (!soloFaltan || !contada(linea)) &&
      (buscado === '' ||
        linea.name.toLowerCase().includes(buscado) ||
        linea.sku.toLowerCase().includes(buscado) ||
        (linea.location ?? '').toLowerCase().includes(buscado)),
  );

  const anular = useMutation({
    mutationFn: () => almacen.cancelCount(conteo.id),
    onSuccess: () => void clientes.invalidateQueries({ queryKey: ['conteo'] }),
  });
  const [confirmaAnular, setConfirmaAnular] = useState(false);

  if (paso === 'revisando') {
    return <Revision conteo={conteo} onRecontar={() => setPaso('contando')} />;
  }

  return (
    <>
      <div className="filtros">
        <span className="mono">{conteo.document}</span>
        <span className="apagado">
          {conteo.scope ?? 'Todo el local'} · lo empezó {conteo.startedBy} el {dia(conteo.startedAt)} a las{' '}
          {hora(conteo.startedAt)}
        </span>
        <span className="recuento">
          {contadas} DE {conteo.lines.length} CONTADOS
        </span>
      </div>

      {sinEncontrar === null ? null : (
        <p className="aviso-caja">
          Ningún {vocabulario.insumo} de este conteo tiene el código <strong>{sinEncontrar}</strong>. Si es de otra
          categoría, no entra en este conteo; si es de aquí, se le guarda el código en su ficha y desde
          entonces se escanea.
        </p>
      )}

      <div className="dos-columnas">
        <div>
          <div className="filtros">
            <Escaner onCodigo={alEscanear} etiqueta="Escanear envase o teclear código" />
            <input
              className="buscador"
              type="search"
              aria-label="Buscar en el conteo"
              placeholder="Buscar…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <Filtro texto="Solo lo que falta" activo={soloFaltan} onClick={() => setSoloFaltan(!soloFaltan)} />
            {familiasDelConteo.length > 0 ? (
              <Filtro texto={vocabulario.porFamilia} activo={porPrenda} onClick={() => setPorPrenda(!porPrenda)} />
            ) : null}
          </div>

          {porPrenda ? (
            <FiltroDeTemporada temporadas={temporadasDe(familiasDelConteo)} elegida={temporada} alElegir={setTemporada} />
          ) : null}
          {porPrenda
            ? deLaTemporada(familiasDelConteo, temporada)
                .filter((familia) => buscado === '' || familia.name.toLowerCase().includes(buscado))
                .map((familia) => (
                  <section key={familia.id} className="tarjeta prenda">
                    <h3 className="tarjeta__titulo">{familia.name}</h3>
                    <MatrizParaTeclear familia={familia} valores={borrador} alTeclear={teclear} rotulo="Contado de" />
                  </section>
                ))
            : null}

          {visibles.length === 0 && porPrenda ? null : visibles.length === 0 ? (
            <Vacio
              titulo={soloFaltan ? 'Ya está todo contado' : 'Nada con esa búsqueda'}
              mensaje={
                soloFaltan
                  ? 'Revisa las diferencias antes de cerrar: es el momento de recontar lo que no cuadre.'
                  : 'Se busca por nombre, por código o por ubicación.'
              }
            />
          ) : (
            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>{mayuscula(vocabulario.insumo)}</th>
                    <th>Dónde</th>
                    <th className="numero">Contado</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((linea) => {
                    const texto = borrador[linea.supplyId] ?? '';
                    const mal = texto.trim() !== '' && Number.isNaN(leerCantidad(texto));
                    const lotes = linea.lots ?? [];
                    const loteALote = porLote.has(linea.supplyId);

                    return (
                      <Fragment key={linea.supplyId}>
                      <tr className={linea.supplyId === ultimo ? 'fila--activa' : ''}>
                        <td>
                          <div className="celda-nombre__texto">
                            <strong>{linea.name}</strong>
                            <span>
                              {linea.sku} · {linea.category}
                            </span>
                          </div>
                        </td>
                        <td className="apagado">{linea.location ?? '—'}</td>
                        <td className="numero">
                          <input
                            className="celda-input"
                            inputMode="decimal"
                            aria-label={`Contado de ${linea.name}, en ${linea.unit}`}
                            aria-invalid={mal}
                            value={texto}
                            disabled={loteALote}
                            onChange={(e) => teclear(linea.supplyId, e.target.value)}
                          />{' '}
                          <span className="apagado">{linea.unit}</span>{' '}
                          {/* Rollos cerrados más los metros del abierto (§2.52). */}
                          <ContarPorEmpaque
                            nombre={linea.name}
                            unidad={linea.unit}
                            empaques={
                              (insumos.data ?? []).find((insumo) => insumo.id === linea.supplyId)?.packages ?? []
                            }
                            onTotal={(total) => teclear(linea.supplyId, total)}
                          />
                          {/*
                           * Lote por lote (migración 273): 40 contadas y 40 en
                           * el libro cuadraban aunque diez fueran del lote que
                           * vence el mes que viene. Contado así, el cierre
                           * ajusta cada lote y no el total.
                           */}
                          {lotes.length === 0 ? null : (
                            <button
                              type="button"
                              className={`opcion ${loteALote ? 'opcion--activa' : ''}`}
                              aria-pressed={loteALote}
                              onClick={() =>
                                setPorLote((previo) => {
                                  const siguiente = new Set(previo);
                                  if (siguiente.has(linea.supplyId)) siguiente.delete(linea.supplyId);
                                  else siguiente.add(linea.supplyId);
                                  return siguiente;
                                })
                              }
                            >
                              Por lote
                            </button>
                          )}
                        </td>
                      </tr>
                      {!loteALote
                        ? null
                        : lotes.map((lote) => {
                            const clave = claveDelLote(linea.supplyId, lote.lotId);
                            const delLote = borrador[clave] ?? '';
                            return (
                              <tr key={clave}>
                                <td>
                                  <span className="apagado">
                                    Lote <span className="mono">{lote.code}</span>
                                    {lote.expiresOn === null ? '' : ` · vence ${fechaDeCalendario(lote.expiresOn)}`}
                                  </span>
                                </td>
                                <td />
                                <td className="numero">
                                  <input
                                    className="celda-input"
                                    inputMode="decimal"
                                    aria-label={`Contado del lote ${lote.code} de ${linea.name}, en ${linea.unit}`}
                                    aria-invalid={delLote.trim() !== '' && Number.isNaN(leerCantidad(delLote))}
                                    value={delLote}
                                    onChange={(e) => teclear(clave, e.target.value)}
                                  />{' '}
                                  <span className="apagado">{linea.unit}</span>
                                </td>
                              </tr>
                            );
                          })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Tarjeta>
          <h2 className="tarjeta__titulo">Cómo va</h2>

          <dl className="resumen-pares">
            <div>
              <dt>Contados</dt>
              <dd className="mono">
                {contadas} de {conteo.lines.length}
              </dd>
            </div>
            <div>
              <dt>Guardado</dt>
              <dd>
                {guardar.isPending
                  ? 'Guardando…'
                  : sinGuardar.size > 0
                    ? `${contados(sinGuardar.size, 'cambio', 'cambios')} por guardar`
                    : 'Todo guardado'}
              </dd>
            </div>
          </dl>

          {invalidas.length > 0 ? (
            <p className="aviso-caja aviso-caja--aviso">
              {contados(invalidas.length, 'cantidad no es', 'cantidades no son')} un número y no se
              guarda: {invalidas.map((linea) => linea.name).join(', ')}.
            </p>
          ) : null}

          {guardar.isError ? <Fallo error={guardar.error} /> : null}

          <p className="nota-sm">
            Puedes dejarlo y seguir después, desde este equipo o desde otro: lo contado ya está
            guardado. Lo que no cuentes no se toca al cerrar.
          </p>

          <Boton
            variante="primario"
            onClick={() => setPaso('revisando')}
            disabled={contadas === 0 || sinGuardar.size > 0 || guardar.isPending}
          >
            Revisar diferencias
          </Boton>

          {confirmaAnular ? (
            <div className="formulario__fila">
              <Boton onClick={() => setConfirmaAnular(false)}>No</Boton>
              <Boton variante="peligro" onClick={() => anular.mutate()} disabled={anular.isPending}>
                {anular.isPending ? 'Anulando…' : 'Sí, anular sin ajustar'}
              </Boton>
            </div>
          ) : (
            <button type="button" className="accion-fila" onClick={() => setConfirmaAnular(true)}>
              Anular el conteo
            </button>
          )}
          {anular.isError ? <Fallo error={anular.error} /> : null}
        </Tarjeta>
      </div>
    </>
  );
}

/**
 * Las diferencias contra la foto, antes de cerrar.
 *
 * Es el paso que separa «me equivoqué contando» de «falta de verdad»: lo que
 * no cuadra se vuelve a mirar antes de que quede en el libro.
 */
function Revision({ conteo, onRecontar }: { conteo: CountSession; onRecontar: () => void }) {
  const vocabulario = useVocabulario();
  const clientes = useQueryClient();
  const revision = useQuery({
    queryKey: ['conteo', conteo.id, 'revision'],
    queryFn: () => almacen.reviewCount(conteo.id),
  });

  // El mismo en cada intento: un reintento tras un corte no ajusta dos veces.
  const [operationId] = useState(newOperationId);

  const cerrar = useMutation({
    mutationFn: () => almacen.closeCountSession(conteo.id, operationId),
    onSuccess: () => void clientes.invalidateQueries(),
  });

  const lineas = revision.data?.lines ?? [];
  const faltantes = useMemo(
    () => lineas.filter((l) => Number.parseFloat(l.value) < 0).reduce((s, l) => s + Number.parseFloat(l.value), 0),
    [lineas],
  );
  const sobrantes = useMemo(
    () => lineas.filter((l) => Number.parseFloat(l.value) > 0).reduce((s, l) => s + Number.parseFloat(l.value), 0),
    [lineas],
  );

  if (cerrar.isSuccess) {
    return (
      <Tarjeta>
        <h2 className="tarjeta__titulo">Conteo cerrado</h2>
        <p className="aviso-caja">
          {conteo.document} dejó {contados(cerrar.data.adjustments, 'ajuste', 'ajustes')} en el kardex.
          Lo que cuadraba no dejó movimiento.
        </p>
      </Tarjeta>
    );
  }

  return (
    <div className="dos-columnas">
      <div className="tabla-envoltura">
        {revision.isLoading ? (
          <Cargando que="las diferencias" />
        ) : revision.isError ? (
          <Fallo error={revision.error} onReintentar={() => revision.refetch()} />
        ) : lineas.length === 0 ? (
          <Vacio titulo="Todo cuadra" mensaje="Lo contado es lo que decía el libro. Cerrar no deja ningún ajuste." />
        ) : (
          <table>
            <thead>
              <tr>
                <th>{mayuscula(vocabulario.insumo)}</th>
                <th className="numero"><ConAyuda ayuda="Lo que el sistema tenía registrado cuando empezó el conteo.">Decía el libro</ConAyuda></th>
                <th className="numero">Contado</th>
                <th className="numero"><ConAyuda ayuda="Contado menos lo que decía el libro. En rojo falta; en verde sobra.">Diferencia</ConAyuda></th>
                <th className="numero"><ConAyuda ayuda="Lo que vale esa diferencia al costo promedio.">Valor</ConAyuda></th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((linea) => {
                const diferencia = Number.parseFloat(linea.difference);
                return (
                  <tr key={`${linea.supplyId}:${linea.lotId ?? ''}`}>
                    <td>
                      <strong>{linea.name}</strong>
                      {linea.lotCode == null ? null : (
                        <span className="apagado">
                          {' '}· lote <span className="mono">{linea.lotCode}</span>
                        </span>
                      )}
                    </td>
                    <td className="numero apagado">
                      {cantidad(linea.expected)} {linea.unit}
                    </td>
                    <td className="numero">
                      {cantidad(linea.counted)} {linea.unit}
                    </td>
                    <td className={`numero ${diferencia < 0 ? 'negativo' : 'positivo'}`}>
                      {diferencia > 0 ? '+' : ''}
                      {cantidad(linea.difference)}
                    </td>
                    <td className="numero apagado">{soles(linea.value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Tarjeta>
        <h2 className="tarjeta__titulo">Cierre del conteo</h2>

        <dl className="resumen-pares">
          <div>
            <dt>Con diferencia</dt>
            <dd className="mono">{lineas.length}</dd>
          </div>
          <div>
            <dt>Sin contar</dt>
            <dd className="mono">{revision.data?.pending ?? 0}</dd>
          </div>
          <div>
            <dt>Faltantes</dt>
            <dd className="mono negativo">{soles(faltantes)}</dd>
          </div>
          <div>
            <dt>Sobrantes</dt>
            <dd className="mono positivo">{soles(sobrantes)}</dd>
          </div>
          <div>
            <dt>Ajuste neto</dt>
            <dd className="mono">{soles(faltantes + sobrantes)}</dd>
          </div>
        </dl>

        {(revision.data?.pending ?? 0) > 0 ? (
          <p className="aviso-caja aviso-caja--aviso">
            {contados(revision.data!.pending, `${vocabulario.insumo} quedó`, `${vocabulario.insumos} quedaron`)} sin contar. No se
            ajustan: se quedan con lo que dice el libro.
          </p>
        ) : null}

        <p className="aviso-caja">
          Al cerrar, cada diferencia queda como un ajuste en el kardex con el documento{' '}
          <span className="mono">{conteo.document}</span>. Si algo no cuadra, recuéntalo antes: es más
          fácil ahora que dentro de un mes. Lo que sobra de algo que exige vencimiento no entra al
          estante: espera en Cuarentena hasta que le pongas su lote y su fecha.
        </p>

        {cerrar.isError ? <Fallo error={cerrar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={onRecontar}>Recontar</Boton>
          <Boton variante="primario" onClick={() => cerrar.mutate()} disabled={cerrar.isPending || revision.isLoading}>
            {cerrar.isPending ? 'Cerrando…' : 'Cerrar y ajustar'}
          </Boton>
        </div>
      </Tarjeta>
    </div>
  );
}

/**
 * `"1234.500"` → `"1234,5"`: como se teclea, sin punto de miles.
 *
 * Con el formato de pantalla, mil servilletas eran «1.000», y al volver a
 * leerlo el punto se tomaba por decimal: el conteo guardaba una.
 */
function paraTeclear(valor: string): string {
  return valor.includes('.') ? valor.replace(/\.?0+$/, '').replace('.', ',') : valor;
}

/** La clave del borrador de un lote: el insumo y el lote. Los UUID no llevan «:». */
function claveDelLote(supplyId: string, lotId: string): string {
  return `${supplyId}:${lotId}`;
}

function partirClave(clave: string): { supplyId: string; lotId: string | null } {
  const [supplyId, lotId] = clave.split(':');
  return { supplyId: supplyId!, lotId: lotId ?? null };
}
